import InstanceTarget from '../../types/instance/InstanceTarget';
import StatusIcon from '../../component/StatusIcon';
import { formatNumber } from '../../helpers/utils';
import { InstanceTileProps, InstanceListRowProps, TileBodyContentProps } from './types';
import Helper from "../../helpers/Helper";
import { Link } from 'react-router-dom';

// Helper component/function to render the tile header content
const TileHeaderContent = ({ instance, onInstanceSelect }: { instance: InstanceTarget, onInstanceSelect: (id: number) => void }) => (
    <div className="tile-header">
        {/* Make name clickable */}
        <span className="instance-name clickable" onClick={() => onInstanceSelect(instance.id)}>
            {instance.name}
        </span>
        {/* Pass instanceId and mode to StatusIcon */}
        <StatusIcon instanceId={instance.id} mode="view" />
    </div>
);

// Helper component/function to render the tile body content (for comfortable)
const TileBodyContent = ({ instance, hostType, metricsDashInsteadOfNA }: TileBodyContentProps) => {
    // Determine DB icon class based on InstanceType component logic
    let dbIconClassName = 'icon icon-database'; // Default
    let dbIconColor = ''; // Default color
    const instanceTypeLower = instance.type?.toLowerCase();
    const instanceVersionLower = instance.version?.toLowerCase();

    if (instanceTypeLower?.includes('sqlserver')) {
        dbIconClassName = 'icon icon-mssql';
    } else if (instanceTypeLower?.includes('mysql')) {
        dbIconClassName = instanceVersionLower?.includes('mariadb') ? 'icon icon-mariadb' : 'icon icon-mysql-alt';
    } else if (instanceTypeLower?.includes('oracle')) {
        dbIconClassName = 'icon icon-oracle';
    } else if (instanceTypeLower?.includes('postgres')) {
        dbIconClassName = 'icon icon-postgres';
    } else if (instanceTypeLower?.includes('cockroach')) {
        dbIconClassName = 'icon fak fa-cockroachdb';
    } else if (instanceTypeLower?.includes('db2')) {
        dbIconClassName = 'icon fak fa-db2';
        dbIconColor = '#04992B';
    } else if (instanceTypeLower?.includes('informix')) {
        dbIconClassName = 'icon fak fa-informix';
        dbIconColor = '#33348F';
    } else if (instanceTypeLower?.includes('saphana')) {
        dbIconClassName = 'icon fak fa-saphana';
        dbIconColor = '#027FC5';
    } else if (instanceTypeLower?.includes('sybase')) {
        dbIconClassName = 'icon fak fa-sapase';
        dbIconColor = '#027FC5';
    } else if (instanceTypeLower?.includes('mongo')) {
        dbIconClassName = 'icon fak fa-mongodb';
        dbIconColor = '#00684A';
    }

    // Determine OS icon and name based on hostType
    let osIconClassName = 'fas fa-server fa-fw'; // Default icon
    let osName = 'N/A'; // Default name to N/A

    if (hostType === 'linux') {
        osIconClassName = 'fab fa-linux fa-fw';
        osName = 'Linux';
    } else if (hostType === 'windows') {
        osIconClassName = 'fab fa-windows fa-fw';
        osName = 'Windows';
    } else if (hostType === 'awsrds') {
        osIconClassName = 'fab fa-aws fa-fw'; // Using AWS icon for RDS
        osName = 'AWS RDS';
    }
    // If hostType is provided but not matched, use capitalized version
    else if (hostType) {
         osName = hostType.charAt(0).toUpperCase() + hostType.slice(1);
         // Keep default icon 'fas fa-server fa-fw'
    }

    return (
        // Remove the outer "comfortable" class here, apply specific styles below
        <div className="tile-body">
            {/* Render info row first */}
            <div className="instance-info-row">
                <span className="instance-type-badge">
                    <i className={dbIconClassName} style={{ color: dbIconColor }} aria-hidden="true"></i> {instance.type || 'N/A'}
                </span>
                <span className="instance-os-badge">
                    <i className={osIconClassName} aria-hidden="true"></i> {osName}
                </span>
            </div>
            {/* Display metrics if available and formatted */}
            {/* Display metrics if available and formatted - Reintroducing Column Wrappers */}
            {instance.metrics && (
                <div className="instance-metrics-grid">
                    {/* Left Column Wrapper (Instance Metrics) */}
                    <div className="metrics-col-left">
                        <div className="metric-item">
                            <span className="metric-label">Total Time:</span>
                            <span className="metric-value">{instance.metrics.totalTime != null ? Helper.getTimeInEnglish(instance.metrics.totalTime) : metricsDashInsteadOfNA ? '-' : 'N/A'}</span>
                        </div>
                        <div className="metric-item">
                            <span className="metric-label">Executions:</span>
                            <span className="metric-value">{instance.metrics.totalExecutions != null ? formatNumber(instance.metrics.totalExecutions) : metricsDashInsteadOfNA ? '-' : 'N/A'}</span>
                        </div>
                        <div className="metric-item">
                            <span className="metric-label">Avg Time:</span>
                            <span className="metric-value">{instance.metrics.averageTime != null ? Helper.getTimeInEnglish(instance.metrics.averageTime) : metricsDashInsteadOfNA ? '-' : 'N/A'}</span>
                        </div>
                    </div>
                    {/* Right Column Wrapper (Host Metrics - Placeholders) */}
                    <div className="metrics-col-right">
                        <div className="metric-item">
                            <span className="metric-label">CPU</span>
                            {/* Display CPU stat or placeholder */}
                            <span className="metric-value">{instance.hostStats?.cpu != null ? `${Math.round(instance.hostStats.cpu)}%` : '--%'}</span>
                        </div>
                        <div className="metric-item">
                            <span className="metric-label">Disk I/O</span>
                            {/* Display IO stat or placeholder */}
                            <span className="metric-value">{instance.hostStats?.io != null ? `${Math.round(instance.hostStats.io)}%` : '--%'}</span>
                        </div>
                        <div className="metric-item">
                            <span className="metric-label">Memory</span>
                            {/* Display Memory stat or placeholder */}
                            <span className="metric-value">{instance.hostStats?.memory != null ? `${Math.round(instance.hostStats.memory)}%` : '--%'}</span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Helper component for the footer with event counts and icons
const TileFooterContent = ({ instance }: { instance: InstanceTarget }) => (
    <div className="tile-footer comfortable">
        {/* Always render the event counts container */}
        <div className="instance-event-counts">
            {/* Using Font Awesome icons based on the image */}
            <Link to={`/instances/${instance.id}/events`} className="event-count-item">
                    <i className="fas fa-exchange-alt event-icon change-icon" aria-hidden="true"></i>
                    {/* Use optional chaining and nullish coalescing */}
                    {instance.eventCounts?.change ?? 0}
                </Link>
                <Link to={`/instances/${instance.id}/events`} className="event-count-item">
                    <i className="far fa-calendar-alt event-icon custom-icon" aria-hidden="true"></i>
                    {/* Use optional chaining and nullish coalescing */}
                    {instance.eventCounts?.custom ?? 0}
                </Link>
                {/* Use optional chaining for the class condition as well */}
                <Link to={`/instances/${instance.id}/events`} className={`event-count-item ${(instance.eventCounts?.alert ?? 0) > 0 ? 'has-alerts' : ''}`}>
                    <i className="fas fa-exclamation-triangle event-icon alert-icon" aria-hidden="true"></i>
                    {/* Use optional chaining and nullish coalescing */}
                    {instance.eventCounts?.alert ?? 0}
                </Link>
            </div>
        {/* Removed the conditional check for instance.eventCounts */}
    </div>
);

// Helper component to render a single row for the list view
const InstanceListRow = ({ instance, statusClass, onInstanceSelect, hostType, allHosts }: InstanceListRowProps) => {
    // --- DB Icon Logic (for Type column) ---
    let dbIconClassName = 'icon icon-database'; // Default
    let dbIconColor = '';
    const instanceTypeLower = instance.type?.toLowerCase();
    const instanceVersionLower = instance.version?.toLowerCase();
    if (instanceTypeLower?.includes('sqlserver')) {
        dbIconClassName = 'icon icon-mssql';
    } else if (instanceTypeLower?.includes('mysql')) {
        dbIconClassName = instanceVersionLower?.includes('mariadb') ? 'icon icon-mariadb' : 'icon icon-mysql-alt';
    } else if (instanceTypeLower?.includes('oracle')) {
        dbIconClassName = 'icon icon-oracle';
    } else if (instanceTypeLower?.includes('postgres')) {
        dbIconClassName = 'icon icon-postgres';
    } else if (instanceTypeLower?.includes('cockroach')) {
        dbIconClassName = 'icon fak fa-cockroachdb';
    } else if (instanceTypeLower?.includes('db2')) {
        dbIconClassName = 'icon fak fa-db2';
        dbIconColor = '#04992B';
    } else if (instanceTypeLower?.includes('informix')) {
        dbIconClassName = 'icon fak fa-informix';
        dbIconColor = '#33348F';
    } else if (instanceTypeLower?.includes('saphana')) {
        dbIconClassName = 'icon fak fa-saphana';
        dbIconColor = '#027FC5';
    } else if (instanceTypeLower?.includes('sybase')) {
        dbIconClassName = 'icon fak fa-sapase';
        dbIconColor = '#027FC5';
    }
    const typeTooltipTargetClass = `instance-type-icon-${instance.id}`;

    // --- OS Icon Logic (similar to TileBodyContent) ---
    let osIconClassName = ''; // Default icon
    let osTooltipText = 'N/A'; // Default tooltip text to N/A

    if (hostType === 'linux') {
        osIconClassName = 'fab fa-linux fa-fw';
        osTooltipText = 'Linux';
    } else if (hostType === 'windows') { // Corrected condition
        osIconClassName = 'fab fa-windows fa-fw';
        osTooltipText = 'Windows';
    } else if (hostType === 'awsrds') {
        osIconClassName = 'fab fa-aws fa-fw'; // Using AWS icon for RDS
        osTooltipText = 'AWS RDS';
    }
    // If hostType is provided but not matched, use capitalized version for tooltip
    else if (hostType) {
        osTooltipText = hostType.charAt(0).toUpperCase() + hostType.slice(1);
        // Keep default icon 'fas fa-server fa-fw'
    }
    // --- Host Name Mapping Logic ---
    let mappedHostName = '-';
    if (instance.hostId && allHosts && Array.isArray(allHosts)) {
        const hostId = Array.isArray(instance.hostId) ? instance.hostId[0] : instance.hostId;
        const mappedHost = allHosts.find(h => h.id === hostId);
        if (mappedHost && mappedHost.name) {
            mappedHostName = mappedHost.name;
        }
    }
    // --- End Host Name Mapping Logic ---

    return (
        // Add the statusClass to the row div
        <div className={`instance-list-row ${statusClass}`}>
            {/* Sensor Status column */}
            <div className="list-col col-status">
                <StatusIcon instanceId={instance.id} mode="view" />
            </div>
            <div className="list-col col-name">
                {/* Make name clickable, remove Link */}
                <span className="instance-name clickable" onClick={() => onInstanceSelect(instance.id)}>
                    {instance.name}
                </span>
            </div>
            {/* Replace text with icon and tooltip - Using Bootstrap */}
            <div className="list-col col-type">
                <i
                    className={`${dbIconClassName} ${typeTooltipTargetClass}`}
                    style={{ color: dbIconColor }}
                    aria-hidden="true"
                    data-bs-toggle="tooltip"
                    data-bs-placement="top"
                    title={instance.type || 'N/A'}
                ></i>
            </div>
            {/* Update host column to show mapped host name only, no icon */}
            <div className="list-col col-host">
                {mappedHostName}
            </div>
            {/* Host Type column: show only the OS icon, no text */}
            <div className="list-col col-hosttype">
                {osIconClassName ? <i
                    className={`${osIconClassName}`}
                    aria-hidden="true"
                    data-bs-toggle="tooltip"
                    data-bs-placement="top"
                    title={osTooltipText}
                ></i> : '-'}
            </div>
            <div className="list-col col-metrics-total-time">
                {instance.metrics?.totalTime != null ? Helper.getTimeInEnglish(instance.metrics.totalTime) : '-'}
            </div>
            <div className="list-col col-metrics-executions">
                {instance.metrics?.totalExecutions != null ? formatNumber(instance.metrics.totalExecutions) : '-'}
            </div>
            <div className="list-col col-metrics-avg-time">
                {instance.metrics?.averageTime != null ? Helper.getTimeInEnglish(instance.metrics.averageTime) : '-'}
            </div>
            {/* New Host Metrics Columns */}
            <div className="list-col col-cpu">{instance.hostStats?.cpu != null ? `${Math.round(instance.hostStats.cpu)}%` : '-'}</div>
            <div className="list-col col-disk">{instance.hostStats?.io != null ? `${Math.round(instance.hostStats.io)}%` : '-'}</div>
            <div className="list-col col-mem">{instance.hostStats?.memory != null ? `${Math.round(instance.hostStats.memory)}%` : '-'}</div>
            {/* Add Separate Event Count Columns */}
            <div className="list-col col-alerts">{instance.eventCounts?.alert ?? 0}</div>
            <div className="list-col col-changes">{instance.eventCounts?.change ?? 0}</div>
            <div className="list-col col-custom">{instance.eventCounts?.custom ?? 0}</div>
            {/* Add Tag columns dynamically if needed */}
        </div>
    );
};


function InstanceTile({ instance, layoutMode, onInstanceSelect, hostType, allHosts }: InstanceTileProps) { // Add allHosts prop

    // Determine the status class based on alerts and instance state/status
    const getStatusClass = (instance: InstanceTarget) => {
        // Red if there are alerts
        if ((instance.eventCounts?.alert ?? 0) > 0) {
            return 'status-alert';
        }
        // Light Grey if state is not 'started' (Offline) or status is not 200 (Issue)
        if (instance.state?.toLowerCase() !== 'started' || instance.status !== 200) {
             return 'status-issue-offline';
        }
        // Green if healthy and no alerts
        return 'status-healthy';
    };

    // Calculate status class once, before conditional rendering
    const currentStatusClass = getStatusClass(instance);

    // Conditional rendering based on layoutMode
    if (layoutMode === 'list') {
        // Render the row component for list view, passing the status class, handler, hostType, and allHosts
        return <InstanceListRow instance={instance} statusClass={currentStatusClass} onInstanceSelect={onInstanceSelect} hostType={hostType} allHosts={allHosts} />;
    } else {
        // Render the tile structure for compact/comfortable views
        return (
            <>
                {/* The actual tile div for compact/comfortable */}
                <div
                    key={instance.id}
                    // Use the calculated status class
                    className={`instance-tile layout-${layoutMode} ${currentStatusClass}`}
                    // Removed react-tooltip attributes
                >
                    {/* Pass handler to header */}
                    <TileHeaderContent instance={instance} onInstanceSelect={onInstanceSelect} />

                    {/* Render comfortable content conditionally OR as hidden tooltip */}
                    {layoutMode === 'comfortable' ? (
                        // Comfortable Mode: Render directly
                        <>
                            <TileBodyContent instance={instance} hostType={hostType} />
                            <TileFooterContent instance={instance} />
                        </>
                    ) : (
                        // Compact Mode: Render hidden tooltip content
                        <div className="compact-tooltip-content">
                            {/* Use a wrapper div that mimics the comfortable tile */}
                            <div className={`instance-tile layout-comfortable ${currentStatusClass}`}>
                                <TileHeaderContent instance={instance} onInstanceSelect={onInstanceSelect} />
                                <TileBodyContent instance={instance} hostType={hostType} />
                                <TileFooterContent instance={instance} />
                            </div>
                        </div>
                    )}
                </div>
            </>
        );
    }
}


export default InstanceTile;
